import React, { useState } from 'react'
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps'
import { ScrollReveal } from './ScrollReveal'

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

const cards = [
  {
    icon: '⚡',
    title: '6-14ms edge latency',
    desc: 'Your API calls hit our nearest APAC node first — Singapore, Sydney, Manila, Jakarta, or Bangkok. Not Virginia.',
  },
  {
    icon: '₱',
    title: 'Bill in your currency',
    desc: 'PHP, SGD, AUD, MYR, IDR, THB, VND, NZD. No USD credit card required. No forex surprises.',
  },
  {
    icon: '💳',
    title: 'Pay your way',
    desc: 'GCash, PayNow, GoPay, OVO, Maya, PromptPay, MoMo, BPAY — we support the payment methods your team actually uses.',
  },
  {
    icon: '🌏',
    title: 'SEA language models',
    desc: 'Models optimized for Tagalog, Bahasa, Thai, Vietnamese, and Singlish. Not just English-first.',
  },
]

const NODES = [
  { id: 'sg', label: 'Singapore', ms: '6ms',  coords: [103.8, 1.35]  as [number,number] },
  { id: 'my', label: 'Kuala Lumpur', ms: '7ms',  coords: [101.7, 3.15]  as [number,number] },
  { id: 'th', label: 'Bangkok',   ms: '7ms',  coords: [100.5, 13.75] as [number,number] },
  { id: 'vn', label: 'Ho Chi Minh', ms: '8ms',coords: [106.7, 10.8]  as [number,number] },
  { id: 'ph', label: 'Manila',    ms: '8ms',  coords: [120.9, 14.6]  as [number,number] },
  { id: 'id', label: 'Jakarta',   ms: '8ms',  coords: [106.8, -6.2]  as [number,number] },
  { id: 'au', label: 'Sydney',    ms: '9ms',  coords: [151.2, -33.9] as [number,number] },
  { id: 'nz', label: 'Auckland',  ms: '14ms', coords: [174.7, -36.9] as [number,number] },
]

// APAC country numeric IDs (ISO 3166-1 numeric)
const APAC_IDS = new Set([
  '764', // Thailand
  '704', // Vietnam
  '458', // Malaysia
  '702', // Singapore
  '608', // Philippines
  '360', // Indonesia
  '036', // Australia
  '554', // New Zealand
  '116', // Cambodia
  '418', // Laos
  '104', // Myanmar
  '096', // Brunei
  '626', // Timor-Leste
])

function PingMarker({ coords, label, ms, delay }: { coords: [number,number]; label: string; ms: string; delay: number }) {
  return (
    <Marker coordinates={coords}>
      {/* Ping rings */}
      <circle r={0} fill="none" stroke="#818cf8" strokeWidth={2.5}>
        <animate attributeName="r" values="0;50" dur="2.5s" begin={`${delay}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0" dur="2.5s" begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
      <circle r={0} fill="none" stroke="#6366f1" strokeWidth={1.5}>
        <animate attributeName="r" values="0;80" dur="2.5s" begin={`${delay + 0.6}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0" dur="2.5s" begin={`${delay + 0.6}s`} repeatCount="indefinite" />
      </circle>
      {/* Core */}
      <circle r={12} fill="#818cf8">
        <animate attributeName="opacity" values="1;0.5;1" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
      <circle r={6} fill="white" />
      {/* Label */}
      <text y={-22} textAnchor="middle" fontSize={14} fill="#c7d2fe" fontFamily="monospace" fontWeight="700">
        {label}
      </text>
      <text y={-8} textAnchor="middle" fontSize={12} fill="#6ee7b7" fontFamily="monospace">
        {ms}
      </text>
    </Marker>
  )
}

export function WhyLocal() {
  return (
    <section className="py-24 bg-[#050810] relative overflow-hidden">
      <div className="absolute right-0 top-1/4 w-[500px] h-[500px] bg-emerald-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container relative z-10">
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Why local matters</h2>
          <p className="mt-4 text-lg text-gray-400">
            Global AI APIs aren't built for APAC businesses. Neoserve is.
          </p>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {cards.map((card, i) => (
              <ScrollReveal key={card.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] transition-colors p-5">
                  <div className="text-2xl mb-3">{card.icon}</div>
                  <h3 className="text-sm font-semibold text-white mb-2">{card.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{card.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Right: Map */}
          <ScrollReveal delay={200}>
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

              <p className="text-xs text-gray-500 font-mono pt-4 text-center tracking-widest uppercase">
                Live Network · APAC Nodes
              </p>

              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center: [130, -10], scale: 420 }}
                style={{ width: '100%', height: 'auto' }}
              >
                <ZoomableGroup zoom={1} minZoom={1} maxZoom={1}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map(geo => {
                        const isApac = APAC_IDS.has(geo.id)
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={isApac ? 'rgba(99,102,241,0.18)' : 'rgba(148,163,184,0.05)'}
                            stroke={isApac ? 'rgba(129,140,248,0.4)' : 'rgba(148,163,184,0.1)'}
                            strokeWidth={isApac ? 0.6 : 0.3}
                            style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                          />
                        )
                      })
                    }
                  </Geographies>

                  {NODES.map((node, i) => (
                    <PingMarker key={node.id} {...node} delay={i * 0.3} />
                  ))}
                </ZoomableGroup>
              </ComposableMap>

              {/* Node latency grid */}
              <div className="grid grid-cols-4 gap-px border-t border-white/[0.06] mx-0">
                {NODES.map((node, i) => (
                  <div
                    key={node.id}
                    className="flex flex-col items-center py-3 px-2 bg-white/[0.01] hover:bg-white/[0.04] transition-colors"
                  >
                    <span className="text-xs font-mono font-bold text-emerald-400">{node.ms}</span>
                    <span className="text-[10px] text-gray-400 mt-0.5 text-center leading-tight">{node.label}</span>
                  </div>
                ))}
              </div>

              <p className="text-center text-xs text-gray-600 font-mono py-2.5">
                Measured from Singapore · Hosted on the edge
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
